import { Box, Paper, Typography } from '@mui/material';
import React from 'react';
import { formatUsdPerMonth } from '../podDashboard.format';
import {
    POD_STATUS_COLORS,
    POD_STATUS_META,
    POD_STATUS_ORDER,
    PodDashboardData,
} from '../podDashboard.types';

interface Props {
    data: PodDashboardData;
}

/** One headline metric in the hero row (right-aligned label under a big value). */
const HeroMetric = ({
    value,
    label,
    align = 'right',
}: {
    value: string;
    label: string;
    align?: 'left' | 'right';
}) => (
    <Box sx={{ textAlign: align }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.1 }}>
            {value}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
            {label}
        </Typography>
    </Box>
);

/**
 * Hero card: total pods + headline money/deploy metrics, a single stacked bar
 * across all statuses, and a legend.
 */
const PodStatusOverviewCard = ({ data }: Props) => {
    const total = data.totalPods || 1; // guard divide-by-zero for bar widths
    const orderedSegments = POD_STATUS_ORDER.map((key) =>
        data.segments.find((s) => s.key === key),
    ).filter((s): s is NonNullable<typeof s> => Boolean(s));

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            {/* Headline row */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '3.5rem', lineHeight: 0.9 }}>
                        {data.totalPods.toLocaleString('en-US')}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', pb: 0.5 }}>
                        total pods purchased / in existence
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 3, md: 5 }, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.1 }}>
                            {formatUsdPerMonth(data.activeMonthlyRevenue)}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
                            active monthly revenue (est., list)
                        </Typography>
                        {data.actualMonthlyRevenue !== null && (
                            <Typography
                                sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}
                            >
                                ≈ {formatUsdPerMonth(data.actualMonthlyRevenue)} actual (Stripe)
                            </Typography>
                        )}
                    </Box>
                    <HeroMetric value={`${data.deployedPercent}%`} label="deployed (subscribed)" />
                </Box>
            </Box>

            {/* Stacked status bar */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: 16,
                    borderRadius: 999,
                    overflow: 'hidden',
                    mt: 3,
                    backgroundColor: 'grey.100',
                }}
            >
                {orderedSegments.map((seg) => {
                    const pct = (seg.podCount / total) * 100;
                    if (pct <= 0) return null;
                    return (
                        <Box
                            key={seg.key}
                            title={`${POD_STATUS_META[seg.key].label} · ${seg.podCount}`}
                            sx={{
                                width: `${pct}%`,
                                backgroundColor: POD_STATUS_COLORS[seg.key],
                            }}
                        />
                    );
                })}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
                {orderedSegments.map((seg) => (
                    <Box key={seg.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '3px',
                                backgroundColor: POD_STATUS_COLORS[seg.key],
                            }}
                        />
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            {POD_STATUS_META[seg.key].label} ·{' '}
                            {seg.podCount.toLocaleString('en-US')}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

export default PodStatusOverviewCard;
